import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import styled, { css, keyframes } from "styled-components";
import { CheckIcon } from "../../assets/icons/checkIcon";
import { ErrorIcon } from "../../assets/icons/errorIcon";
import { designSystemVars as vars } from "../tokens";
import { ButtonBase } from "./Button";

export type NotificationType = "error" | "success";

export interface NotificationProps {
  message: string;
  type: NotificationType;
  id: number;
  actionName?: string;
  onActionClick?: () => void;
}

const NOTIFICATION_VIEWPORT_ATTRIBUTE = "data-notification-viewport";
const MAX_VISIBLE_NOTIFICATIONS = 2;
const activeNotifications: symbol[] = [];
const stackListeners = new Set<() => void>();
let stackVersion = 0;
let nextNotificationInstanceId = 0;

const notifyStackListeners = () => {
  stackVersion += 1;
  stackListeners.forEach((listener) => listener());
};

const activateNotification = (notificationId: symbol) => {
  const currentIndex = activeNotifications.indexOf(notificationId);
  if (currentIndex !== -1) activeNotifications.splice(currentIndex, 1);
  activeNotifications.unshift(notificationId);
  notifyStackListeners();
};

const deactivateNotification = (notificationId: symbol) => {
  const currentIndex = activeNotifications.indexOf(notificationId);
  if (currentIndex === -1) return;
  activeNotifications.splice(currentIndex, 1);
  notifyStackListeners();
};

const subscribeToStack = (listener: () => void) => {
  stackListeners.add(listener);
  return () => stackListeners.delete(listener);
};

const getStackVersion = () => stackVersion;

const getNotificationViewport = () => {
  if (typeof document === "undefined") return null;

  const existingViewport = document.querySelector<HTMLElement>(
    `[${NOTIFICATION_VIEWPORT_ATTRIBUTE}]`,
  );
  if (existingViewport) return existingViewport;

  const viewport = document.createElement("div");
  viewport.setAttribute(NOTIFICATION_VIEWPORT_ATTRIBUTE, "");
  Object.assign(viewport.style, {
    position: "fixed",
    zIndex: vars.zIndex.toast,
    top: "70px",
    right: vars.spacing.xl,
    display: "flex",
    width: "400px",
    maxWidth: "calc(100vw - 48px)",
    boxSizing: "border-box",
    flexDirection: "column",
    gap: vars.spacing.sm,
    pointerEvents: "none",
  });
  document.body.appendChild(viewport);
  return viewport;
};

const notificationBorder = (type: NotificationType) => {
  return type === "error"
    ? vars.color.feedback.error.border
    : vars.color.feedback.success.border;
};

const notificationBackground = (type: NotificationType) => {
  return type === "error"
    ? vars.color.feedback.error.background
    : vars.color.feedback.success.background;
};

const notificationEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const NotificationRoot = styled.div<{
  $type: NotificationType;
  $stackPosition: number;
  $isNewest: boolean;
}>`
  position: relative;
  order: ${({ $stackPosition }) => $stackPosition};
  display: flex;
  width: 100%;
  box-sizing: border-box;
  align-items: flex-start;
  padding: ${vars.spacing.lg};
  border: ${vars.borderWidth.medium} solid
    ${({ $type }) => notificationBorder($type)};
  border-radius: ${vars.radius.xl};
  background: ${({ $type }) => notificationBackground($type)};
  box-shadow: ${vars.shadow.dropdown};
  color: ${vars.color.text.primary};
  pointer-events: auto;

  ${({ $isNewest }) =>
    $isNewest &&
    css`
      animation: ${notificationEnter} ${vars.motion.fast} ${vars.motion.easing};
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const NotificationContent = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: flex-start;
  gap: ${vars.spacing.sm};
`;

const NotificationText = styled.p`
  min-width: 0;
  flex: 1;
  margin: 0;
  padding-top: ${vars.spacing.xs};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.md};
  font-weight: ${vars.typography.weight.medium};
  line-height: ${vars.typography.lineHeight.px25_5};
`;

const NotificationAction = styled(ButtonBase)`
  flex: 0 0 auto;
  padding: ${vars.spacing.xs};
  border: 0;
  background: var(--inzeloop-ds-color-background-transparent);
  color: ${vars.color.text.primary};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.sm};
  font-weight: ${vars.typography.weight.semibold};
  line-height: var(--inzeloop-ds-line-height-25-5px);
  cursor: default;
`;

const NotificationIcon = ({ type }: { type: NotificationType }) => {
  if (type === "success") return <CheckIcon />;
  return <ErrorIcon size={32} />;
};

interface NotificationInstance {
  key: number;
  instanceId: symbol;
  message: string;
  type: NotificationType;
  actionName?: string;
  onActionClick?: () => void;
}

export const Notification = ({
  message,
  type,
  id,
  actionName,
  onActionClick,
}: NotificationProps) => {
  const [notifications, setNotifications] = useState<NotificationInstance[]>(
    [],
  );
  const [viewport, setViewport] = useState<HTMLElement | null>(null);
  const notificationRef = useRef({ message, type, actionName, onActionClick });
  const timers = useRef(new Map<symbol, number>());
  notificationRef.current = { message, type, actionName, onActionClick };

  useSyncExternalStore(subscribeToStack, getStackVersion, getStackVersion);

  useEffect(() => {
    const notification = notificationRef.current;
    if (!notification.message) return;

    const instanceId = Symbol("notification-instance");
    const instance = {
      key: ++nextNotificationInstanceId,
      instanceId,
      ...notification,
    };

    setViewport(getNotificationViewport());
    setNotifications((current) => [instance, ...current]);
    activateNotification(instanceId);

    const timer = window.setTimeout(() => {
      setNotifications((current) =>
        current.filter((item) => item.instanceId !== instanceId),
      );
      timers.current.delete(instanceId);
      deactivateNotification(instanceId);
    }, 5000);
    timers.current.set(instanceId, timer);
  }, [id]);

  useEffect(() => {
    const activeTimers = timers.current;

    return () => {
      activeTimers.forEach((timer, instanceId) => {
        window.clearTimeout(timer);
        deactivateNotification(instanceId);
      });
      activeTimers.clear();
    };
  }, []);

  if (!viewport) return null;

  const visibleNotifications = notifications
    .map((notification) => ({
      ...notification,
      stackPosition: activeNotifications.indexOf(notification.instanceId),
    }))
    .filter(
      ({ stackPosition }) =>
        stackPosition >= 0 && stackPosition < MAX_VISIBLE_NOTIFICATIONS,
    );

  if (visibleNotifications.length === 0) return null;

  return createPortal(
    <>
      {visibleNotifications.map((notification) => (
        <NotificationRoot
          key={notification.key}
          role={notification.type === "error" ? "alert" : "status"}
          $type={notification.type}
          $stackPosition={notification.stackPosition}
          $isNewest={notification.stackPosition === 0}
        >
          <NotificationContent>
            <NotificationIcon type={notification.type} />
            <NotificationText>{notification.message}</NotificationText>
            {notification.actionName && notification.onActionClick && (
              <NotificationAction onClick={notification.onActionClick}>
                {notification.actionName}
              </NotificationAction>
            )}
          </NotificationContent>
        </NotificationRoot>
      ))}
    </>,
    viewport,
  );
};
